import {
  Box,
  Typography,
  Grid,
  CardMedia,
  Button,
  Paper,
  Snackbar,
  Alert,
  InputBase,
} from "@mui/material";
import { useCart } from "../../context/CartContext";
import { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import ProductViewer from "../../components/ProductViewer";

export default function ProductDetail({ product, onBack }) {
  const { user } = useAuth();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);
  const [open, setOpen] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);

  const originalVariant = {
    variantSku: product.sku + product.name + "Original",
    variantName: "Original",
    variantColor: "#000000",
    variantImage: product.image,
    variantQuantity: product.quantity,
  };

  const variants = [originalVariant, ...(product.variants || [])];

  const handleAdd = () => {
    const chosen = selectedVariant || originalVariant;

    const itemToAdd = {
      sku: chosen.variantSku,
      productSku: product.sku,
      productName: product.name,
      variantName: chosen.variantName,
      price: user?.taxId ? product.priceWholesale : product.priceUnitary,
      quantity: qty,
      image: chosen.variantImage,
    };

    addItem(itemToAdd, qty);
    setOpen(true);
  };

  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 4 } }}>
      <Grid container spacing={{ xs: 3, md: 5 }} sx={{ width: "100%" }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              borderRadius: { xs: 4, md: 6 },
              overflow: "hidden",
              boxShadow: "0 30px 90px rgba(34, 41, 33, 0.14)",
              maxHeight: { md: "80vh" },
              maxWidth: { md: "80vh" },
              bgcolor: "#F3EDE3",
              border: "1px solid rgba(35,45,37,.12)",
            }}
          >
            <Box
              sx={{
                width: "100%",
                aspectRatio: "1 / 1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: { xs: 2.5, md: 5 },
                overflow: "hidden",
                background:
                  "radial-gradient(circle at 25% 15%, rgba(255,255,255,.9), transparent 38%), linear-gradient(145deg, #f7f0e5, #e8dccb)",
              }}
            >
              <ProductViewer
                src={(selectedVariant || originalVariant).variantImage}
                alt={product.name}
                title={`${product.name} • ${(selectedVariant || originalVariant).variantName}`}
              >
                <CardMedia
                  component="img"
                  image={(selectedVariant || originalVariant).variantImage}
                  alt={product.name}
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    bgcolor: "#fff",
                    borderRadius: { xs: 2.5, md: 3.5 },
                    border: "1px solid rgba(35,45,37,.08)",
                    boxShadow:
                      "0 28px 70px rgba(86,68,45,.16), inset 0 0 0 8px rgba(255,255,255,.72)",
                    p: { xs: 1.5, md: 2.5 },
                    transition: "transform 0.35s ease",
                    cursor: "zoom-in",
                    "&:hover": { transform: "scale(1.05)" },
                  }}
                />
              </ProductViewer>
            </Box>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ px: { xs: 0, md: 2 }, pt: { md: 2 } }}>
            <Typography variant="h4" fontWeight={900} gutterBottom sx={{ fontFamily: '"DM Serif Display", serif', fontSize: { xs: "2.35rem", md: "3.4rem" }, lineHeight: .95 }}>
              {product.name}
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "baseline" }}>
              <Typography variant="h5" color="text.secondary" gutterBottom fontWeight={800}>
                SKU: {product.sku}
              </Typography>

              <Typography
                variant="h4"
                color="primary"
                fontWeight={900}
                gutterBottom
                sx={{ fontFamily: '"DM Serif Display", serif' }}
              >
                $
                {user?.taxId
                  ? product.priceWholesale.toFixed(2)
                  : product.priceUnitary.toFixed(2)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Typography variant="h6" color="text.secondary" gutterBottom fontWeight={800}>
                Weight: {product.weight},
              </Typography>
              <Typography variant="h6" color="text.secondary" gutterBottom fontWeight={800}>
                Size: {product.size}
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary">
              {product.description}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {product?.specialSpecifications}
            </Typography>
            <Box
              sx={{
                mt: 3,
                mb: 3,
                p: 2,
                borderRadius: 2,
                border: "1px solid rgba(35,45,37,.12)",
                bgcolor: "rgba(255,253,248,0.92)",
                boxShadow: "0 16px 45px rgba(35,45,37,.08)",
              }}
            >
              <Typography variant="subtitle1" fontWeight={600} mb={1}>
                Choose color
              </Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
                {variants.map((variant) => {
                  const isSelected =
                    selectedVariant?.variantSku === variant.variantSku ||
                    (!selectedVariant &&
                      variant.variantSku === originalVariant.variantSku);

                  return (
                    <Box
                      key={variant.variantSku}
                      onClick={() => setSelectedVariant(variant)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        border: isSelected
                          ? "2px solid #000"
                          : "1px solid rgba(0,0,0,0.2)",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        bgcolor: isSelected
                          ? "rgba(0,0,0,0.05)"
                          : "transparent",

                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: variant.variantColor,
                          border: "1px solid rgba(0,0,0,0.3)",
                        }}
                      />
                      <Typography
                        fontSize={14}
                        fontWeight={isSelected ? 700 : 500}
                      >
                        {variant.variantName}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 2,
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #ccc",
                    borderRadius: "5px",
                    bgcolor: "white",
                    height: 48,
                    boxShadow:
                      "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
                  }}
                >
                  <Button
                    onClick={() => setQty((prev) => Math.max(1, prev - 1))}
                    sx={{
                      minWidth: 40,
                      height: "100%",
                      fontSize: 20,
                      borderRadius: 0,
                    }}
                  >
                    −
                  </Button>

                  <InputBase
                    type="number"
                    value={qty}
                    onChange={(e) => {
                      const value = parseInt(e.target.value, 10);
                      if (isNaN(value)) return;
                      setQty(Math.max(1, value));
                    }}
                    inputProps={{
                      min: 1,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: {
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: 16,
                        width: 50,
                      },
                    }}
                    sx={{
                      height: "100%",
                      px: 1,
                      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                        {
                          WebkitAppearance: "none",
                          margin: 0,
                        },
                      "& input[type=number]": {
                        MozAppearance: "textfield",
                      },
                    }}
                  />

                  <Button
                    onClick={() => setQty((prev) => prev + 1)}
                    sx={{
                      minWidth: 40,
                      height: "100%",
                      fontSize: 20,
                      borderRadius: 0,
                    }}
                  >
                    +
                  </Button>
                </Box>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleAdd}
                  sx={{
                    borderRadius: "5px",
                    textTransform: "none",
                    width: "100%",
                    fontWeight: "bold",
                    px: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    height: "48px",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  🛒 Add to Cart
                </Button>
              </Box>

              <Button
                variant="contained"
                size="large"
                onClick={onBack}
                sx={{
                  borderRadius: "5px",
                  textTransform: "none",
                  width: "100%",
                  fontWeight: "bold",
                  px: 2,
                  bgcolor: "rgba(255, 0, 0, 1)",
                  "&:hover": { bgcolor: "rgba(116, 3, 3, 1)" },
                }}
              >
                ← Back To All Products
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={open}
        autoHideDuration={3000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setOpen(false)} severity="success">
          {product.name} {(selectedVariant || originalVariant).variantName}{" "}
          added to cart!
        </Alert>
      </Snackbar>
    </Box>
  );
}
